import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import Points from 'components/Points';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import {
  LOGO_ASSETS,
  ICON_SET_ASSETS,
  BANNER_ASSETS,
  APP_ICON_ASSETS,
  COLOR_TOKENS,
  TYPEFACES,
  USAGE_DOS,
  USAGE_DONTS,
  DEFERRED_ASSETS,
  POINT_COIN_SRC,
  POINT_GLYPH,
  POINT_GLYPH_CODEPOINT,
  POINT_TOKENS,
  POINT_SIMPLIFIED_CSS,
  BRAND_GRADIENT,
  BrandAsset,
} from './brandData';
import './brand.css';
import { copyToClipboard } from 'utils/copyToClipboard';

// Small hook for click to copy with a short confirmation window.
const useCopy = () => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = useCallback(async (value: string, key: string) => {
    try {
      if (!(await copyToClipboard(value))) return;
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // Clipboard can be blocked in some contexts; fail quietly.
    }
  }, []);

  return { copiedKey, copy };
};

interface CopyChipProps {
  value: string;
  copyKey: string;
  copiedKey: string | null;
  onCopy: (value: string, key: string) => void;
  className?: string;
  children?: React.ReactNode;
}

const CopyChip: React.FC<CopyChipProps> = ({
  value,
  copyKey,
  copiedKey,
  onCopy,
  className,
  children,
}) => {
  const isCopied = copiedKey === copyKey;
  return (
    <button
      type="button"
      className={`brand-copy-chip ${className || ''} ${isCopied ? 'copied' : ''}`}
      onClick={() => onCopy(value, copyKey)}
      title="Click to copy"
    >
      <span className="brand-copy-chip-value">{children ?? value}</span>
      <span className="brand-copy-chip-hint">{isCopied ? 'Copied' : 'Copy'}</span>
    </button>
  );
};

const AssetCard: React.FC<{ asset: BrandAsset }> = ({ asset }) => (
  <div className="brand-asset-card">
    <div className={`brand-asset-preview ${asset.previewBg}`}>
      <img
        className={asset.scaleUp ? 'brand-asset-img-scale' : ''}
        src={asset.preview}
        alt={asset.label}
        loading="lazy"
      />
    </div>
    <div className="brand-asset-meta">
      <div className="brand-asset-label">{asset.label}</div>
      {asset.description && (
        <div className="brand-asset-desc">{asset.description}</div>
      )}
      <a
        className="brand-asset-download"
        href={asset.href}
        download
        target="_blank"
        rel="noopener noreferrer"
      >
        Download {asset.format}
      </a>
    </div>
  </div>
);

const BrandPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { copiedKey, copy } = useCopy();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // The text fallback drops the pill entirely: just the eternity glyph and the
  // gold number in Georgia. Sizes mirror the small and large point variants.
  const renderFallback = (size: 'small' | 'big') => {
    const glyphPx = size === 'big' ? POINT_TOKENS.bigCoinPx : POINT_TOKENS.smallCoinPx;
    const fontSize = size === 'big' ? POINT_TOKENS.bigFontSize : POINT_TOKENS.smallFontSize;
    return (
      <div className="brand-point-fallback">
        <span
          className="brand-point-glyph"
          style={{ fontSize: `${glyphPx}px` }}
          aria-hidden="true"
        >
          {POINT_GLYPH}
        </span>
        <span className="brand-point-num-bare" style={{ fontSize }}>
          1,250
        </span>
      </div>
    );
  };

  return (
    <>
      <SEOHelmet
        title="Brand Assets - TradeMagic | Logos, Colors, and Usage"
        description="Official TradeMagic brand assets for partners. Download approved logos, copy exact color and typography tokens, and reproduce the point component correctly."
        canonical="https://trademagic.gg/brand"
        keywords="TradeMagic brand assets, TradeMagic logo, brand guidelines, partner assets, point component, brand colors"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Brand', url: 'https://trademagic.gg/brand' },
        ]}
      />

      <div className="brand-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="brand-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="brand-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/brand', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="brand-main">
            <TopNavigationBar
              title="Brand"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="brand-container">
              <header className="brand-header">
                <h1 className="brand-title">Brand Assets</h1>
                <p className="brand-subtitle">
                  Approved logos, colors, typography, and the point component for
                  partners placing TradeMagic in their products. Download the
                  files you need and copy the exact values shown here.
                </p>
              </header>

              {/* Logos */}
              <section className="brand-section">
                <h2 className="brand-section-title">Logos</h2>
                <p className="brand-text">
                  Use the black logo on light backgrounds. Keep clear space around
                  the logo equal to the height of the icon, and do not place it
                  smaller than 24 pixels tall. For dark backgrounds, use the white
                  icon from the set below.
                </p>
                <div className="brand-asset-grid">
                  {LOGO_ASSETS.map((asset) => (
                    <AssetCard key={asset.label} asset={asset} />
                  ))}
                </div>

                <h3 className="brand-subsection-title">Icons</h3>
                <p className="brand-text">
                  The swirl mark on its own, without the wordmark. Use an icon
                  where space is tight or the brand is already established nearby.
                </p>
                <div className="brand-asset-grid">
                  {ICON_SET_ASSETS.map((asset) => (
                    <AssetCard key={asset.label} asset={asset} />
                  ))}
                </div>
              </section>

              {/* Banner */}
              <section className="brand-section">
                <h2 className="brand-section-title">Banner lockup</h2>
                <p className="brand-text">
                  The horizontal lockup pairs the icon with the trademagic.gg
                  wordmark. Use it for headers and link previews.
                </p>
                <div className="brand-asset-grid">
                  {BANNER_ASSETS.map((asset) => (
                    <AssetCard key={asset.label} asset={asset} />
                  ))}
                </div>
              </section>

              {/* App icons */}
              <section className="brand-section">
                <h2 className="brand-section-title">App icons</h2>
                <p className="brand-text">
                  Platform files for store listings, web app manifests, the
                  browser tab, and the iOS home screen. These are fixed-purpose
                  raster assets. For the brand mark itself, use the Icons above.
                </p>
                <div className="brand-asset-grid">
                  {APP_ICON_ASSETS.map((asset) => (
                    <AssetCard key={asset.label} asset={asset} />
                  ))}
                </div>
              </section>

              {/* Point component */}
              <section className="brand-section">
                <h2 className="brand-section-title">Point component</h2>
                <p className="brand-text">
                  The point value shows a player balance as a gold coin next to an
                  integer. There are two ways to reproduce it. Use the coin recipe
                  by default. Use the text recipe only when you cannot embed an
                  image.
                </p>

                <div className="brand-point-grid">
                  <div className="brand-point-card preferred">
                    <div className="brand-point-card-tag preferred">Preferred</div>
                    <div className="brand-point-card-title">Coin, pixel accurate</div>
                    <div className="brand-point-samples">
                      <div className="brand-point-sample">
                        <Points type="small" points={1250} />
                        <span className="brand-point-sample-label">Small, 11px</span>
                      </div>
                      <div className="brand-point-sample">
                        <Points type="big" points={1250} />
                        <span className="brand-point-sample-label">Large, 14px</span>
                      </div>
                    </div>
                    <p className="brand-point-note">
                      Embed the coin SVG next to the integer. This is the live
                      point component, so it matches the rendering on the site.
                    </p>
                    <a
                      className="brand-asset-download"
                      href={POINT_COIN_SRC}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download coin SVG
                    </a>
                  </div>

                  <div className="brand-point-card">
                    <div className="brand-point-card-tag fallback">Fallback</div>
                    <div className="brand-point-card-title">Text, no image</div>
                    <div className="brand-point-samples">
                      <div className="brand-point-sample">
                        {renderFallback('small')}
                        <span className="brand-point-sample-label">Small, 11px</span>
                      </div>
                      <div className="brand-point-sample">
                        {renderFallback('big')}
                        <span className="brand-point-sample-label">Large, 14px</span>
                      </div>
                    </div>
                    <p className="brand-point-note">
                      No pill, just the eternity glyph and the gold number. The
                      coin is the arevakhach, so the glyph reproduces the same
                      symbol in text. Use it when an image is not an option.
                    </p>
                    <div className="brand-glyph-row">
                      <CopyChip
                        value={POINT_GLYPH}
                        copyKey="glyph"
                        copiedKey={copiedKey}
                        onCopy={copy}
                        className="brand-glyph-chip"
                      >
                        <span className="brand-glyph">{POINT_GLYPH}</span>
                        <span className="brand-glyph-code">{POINT_GLYPH_CODEPOINT}</span>
                      </CopyChip>
                    </div>
                  </div>
                </div>

                <h3 className="brand-subsection-title">Exact values</h3>
                <div className="brand-token-list">
                  <CopyChip value={POINT_TOKENS.numberColor} copyKey="pt-color" copiedKey={copiedKey} onCopy={copy}>
                    Number color {POINT_TOKENS.numberColor}
                  </CopyChip>
                  <CopyChip value={POINT_TOKENS.font} copyKey="pt-font" copiedKey={copiedKey} onCopy={copy}>
                    Font {POINT_TOKENS.font}
                  </CopyChip>
                  <CopyChip value={POINT_TOKENS.smallFontSize} copyKey="pt-small" copiedKey={copiedKey} onCopy={copy}>
                    Small size {POINT_TOKENS.smallFontSize}
                  </CopyChip>
                  <CopyChip value={POINT_TOKENS.bigFontSize} copyKey="pt-big" copiedKey={copiedKey} onCopy={copy}>
                    Large size {POINT_TOKENS.bigFontSize}
                  </CopyChip>
                  <CopyChip value={POINT_TOKENS.pillGradient} copyKey="pt-grad" copiedKey={copiedKey} onCopy={copy}>
                    Pill gradient
                  </CopyChip>
                  <CopyChip value={POINT_TOKENS.pillInner} copyKey="pt-inner" copiedKey={copiedKey} onCopy={copy}>
                    Inner background {POINT_TOKENS.pillInner}
                  </CopyChip>
                </div>

                <h3 className="brand-subsection-title">Text recipe</h3>
                <div className="brand-code-block">
                  <button
                    type="button"
                    className={`brand-code-copy ${copiedKey === 'pt-css' ? 'copied' : ''}`}
                    onClick={() => copy(POINT_SIMPLIFIED_CSS, 'pt-css')}
                  >
                    {copiedKey === 'pt-css' ? 'Copied' : 'Copy'}
                  </button>
                  <pre>{POINT_SIMPLIFIED_CSS}</pre>
                </div>
              </section>

              {/* Color tokens */}
              <section className="brand-section">
                <h2 className="brand-section-title">Colors</h2>
                <p className="brand-text">
                  Click any value to copy it. The brand gradient runs from purple
                  to the pink accent.
                </p>
                <div
                  className="brand-gradient-bar"
                  style={{ background: BRAND_GRADIENT }}
                >
                  <CopyChip
                    value="linear-gradient(90deg, #6F51E4 0%, #E02CDC 100%)"
                    copyKey="gradient"
                    copiedKey={copiedKey}
                    onCopy={copy}
                    className="brand-gradient-chip"
                  >
                    Copy brand gradient
                  </CopyChip>
                </div>
                <div className="brand-swatch-grid">
                  {COLOR_TOKENS.map((token) => (
                    <button
                      type="button"
                      key={token.hex + token.name}
                      className="brand-swatch"
                      style={{ background: token.hex }}
                      onClick={() => copy(token.hex, `c-${token.hex}-${token.name}`)}
                      title="Click to copy"
                    >
                      <span
                        className={`brand-swatch-body ${token.swatchText === 'dark' ? 'on-light' : 'on-dark'}`}
                      >
                        <span className="brand-swatch-name">{token.name}</span>
                        <span className="brand-swatch-hex">
                          {copiedKey === `c-${token.hex}-${token.name}` ? 'Copied' : token.hex}
                        </span>
                        <span className="brand-swatch-usage">{token.usage}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              {/* Typography */}
              <section className="brand-section">
                <h2 className="brand-section-title">Typography</h2>
                <p className="brand-text">
                  Plus Jakarta Sans carries interface text, Crimson Pro sets
                  headings, Beleren styles Magic card text, and Georgia renders the
                  point value.
                </p>
                <div className="brand-type-list">
                  {TYPEFACES.map((face) => (
                    <div className="brand-type-row" key={face.name}>
                      <div
                        className="brand-type-sample"
                        style={{ fontFamily: face.fontFamily }}
                      >
                        {face.sample}
                      </div>
                      <div className="brand-type-meta">
                        <span className="brand-type-name">{face.name}</span>
                        <span className="brand-type-usage">{face.usage}</span>
                        <span className="brand-type-source">{face.source}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Usage */}
              <section className="brand-section">
                <h2 className="brand-section-title">Usage</h2>
                <div className="brand-usage-grid">
                  <div className="brand-usage-col do">
                    <h3 className="brand-usage-title">Do</h3>
                    <ul className="brand-usage-list">
                      {USAGE_DOS.map((rule) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="brand-usage-col dont">
                    <h3 className="brand-usage-title">Do not</h3>
                    <ul className="brand-usage-list">
                      {USAGE_DONTS.map((rule) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Deferred */}
              <section className="brand-section">
                <h2 className="brand-section-title">Coming later</h2>
                <p className="brand-text">
                  The following assets are not published yet and can be added when
                  ready. Ask the TradeMagic team if you need one before then.
                </p>
                <ul className="brand-deferred-list">
                  {DEFERRED_ASSETS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default BrandPage;
